import styled from "@emotion/styled";
import { Paper } from "@mui/material";
import get from "lodash.get";
import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";

import LinePercentage from "components/LinePercentage";
import Placeholder from "components/Placeholder";
import InOutDistribution from "scenes/Flow/components/InOutDistribution";
import Abilities from "services/permissions/abilities";
import ParkkiTheme from "themes/ParkkiTheme";

const Container = styled(Paper)`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100px;
`;

const LinePercentageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  flex: 1;
  padding: ${ParkkiTheme.spacings.base};
  padding-bottom: 0px;
`;

const LinePercentageTitle = styled.div`
  font-size: ${ParkkiTheme.fontSizes.small};
  font-weight: bold;
  margin-bottom: ${ParkkiTheme.spacings.small};
`;

const GraphsContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: ${ParkkiTheme.spacings.base};
`;

const DisabledOverlay = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-color: black;
  z-index: 10;
  opacity: 0.6;
  display: flex;
  color: rgba(255, 255, 255, 0.7);
  align-items: center;
  justify-content: center;
`;

const UnavailableLabel = styled.div`
  -ms-transform: rotate(-10deg);
  transform: rotate(-10deg);
`;

function InOutDistributionLayout({
  disabled,
  initialized,
  stats,
  trafficDetails,
  inOutDistributionByPercentage,
}) {
  const intl = useIntl();
  const type = useMemo(() => stats?.type, [stats]);

  const toggleLinePercentage = useMemo(() => {
    if (
      type !== "point" ||
      !Abilities.ability.can("read", "flow.distributionPoint.percentageBar") ||
      Number.isNaN(stats?.percentageIn) ||
      Number.isNaN(stats?.percentageOut)
    ) {
      return false;
    }

    return true;
  }, [stats?.percentageIn, stats?.percentageOut, type]);

  const percentageIn = useMemo(() => {
    return {
      percentage: Math.round((stats?.percentageIn || 0) * 100),
      value: stats?.in
        ?.map((inData) => inData.value)
        ?.reduce((acc, cur) => acc + cur, 0),
    };
  }, [stats?.in, stats?.percentageIn]);

  const percentageOut = useMemo(() => {
    return {
      percentage: Math.round((stats?.percentageOut || 0) * 100),
      value: stats?.out
        ?.map((outData) => outData.value)
        ?.reduce((acc, cur) => acc + cur, 0),
    };
  }, [stats?.out, stats?.percentageOut]);

  if (!initialized) {
    return <Placeholder height="320px" delay={0} />;
  }

  const isTotalInVehiclesZero =
    stats?.in?.reduce((acc, curr) => acc + curr?.value ?? 0, 0) === 0;
  const isTotalOutVehiclesZero =
    stats?.out?.reduce((acc, curr) => acc + curr?.value ?? 0, 0) === 0;

  return (
    <Container>
      {toggleLinePercentage && (
        <LinePercentageContainer>
          <LinePercentageTitle>
            {intl.formatMessage(
              { id: "flowScene.inOutDistribution.linePercentageTitle" },
              {
                in:
                  trafficDetails?.labels?.in ??
                  intl.messages["flowScene.inOutDistribution.in"],
                out:
                  trafficDetails?.labels?.out ??
                  intl.messages["flowScene.inOutDistribution.out"],
              }
            )}
          </LinePercentageTitle>
          <LinePercentage
            leftContent={percentageIn}
            rightContent={percentageOut}
            byPercentage={inOutDistributionByPercentage}
          />
        </LinePercentageContainer>
      )}
      <GraphsContainer>
        {(disabled || (isTotalInVehiclesZero && isTotalOutVehiclesZero)) && (
          <DisabledOverlay>
            <UnavailableLabel>
              {intl.messages["toast.info.unavailable"]}
            </UnavailableLabel>
          </DisabledOverlay>
        )}
        {get(stats, "in", []).length > 0 &&
          (type === "point"
            ? Abilities.ability.can("read", "flow.distributionPoint.threshold")
            : true) &&
          !isTotalInVehiclesZero && (
            <InOutDistribution
              disabled={stats?.availableFlowTypes?.length <= 1}
              type={type}
              title={
                trafficDetails?.labels?.in ??
                intl.messages["flowScene.inOutDistribution.in"]
              }
              stats={get(stats, "in", [])}
            />
          )}
        {get(stats, "out", []).length > 0 &&
          (type === "point"
            ? Abilities.ability.can("read", "flow.distributionPoint.threshold")
            : true) &&
          !isTotalOutVehiclesZero && (
            <InOutDistribution
              disabled={stats?.availableFlowTypes?.length <= 1}
              type={type}
              title={
                trafficDetails?.labels?.out ??
                intl.messages["flowScene.inOutDistribution.out"]
              }
              stats={get(stats, "out", [])}
            />
          )}
      </GraphsContainer>
    </Container>
  );
}

const mapStateToProps = (state) => ({
  inOutDistributionByPercentage:
    state?.Flow.filters.inOutDistributionByPercentage,
});

export default connect(mapStateToProps)(InOutDistributionLayout);
